import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Upload,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Search,
  Eye,
  Copy
} from "lucide-react";
import api from "../utils/api";
import PageHeader from "../components/PageHeader";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";

const SAMPLE_SKILL_MD = `---
name: "customer_support_skill"
description: "Handles customer inquiries, order refunds, and product returns."
---

# Customer Support Skill

## Role & Goal
You are a Customer Support Specialist. Your goal is to guide customers through return policies and refund inquiries accurately.

## Workflow Rules
1. Ask for Order ID and return reason.
2. Check 30-day return eligibility window.
3. Respond in clean markdown bullet points.`;

const Skills = ({ activeAiAgentId }) => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState("");

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    instructions: "",
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // File Upload State
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fetchSkills = useCallback(async () => {
    setLoading(true);
    try {
      if (activeAiAgentId) {
        api.setAiAgentId(activeAiAgentId);
      }
      const data = await api.get("/skills");
      setSkills(data || []);
    } catch (err) {
      console.error("Error fetching skills:", err);
      if (showToast) showToast("Failed to load skills for this agent", "error");
    } finally {
      setLoading(false);
    }
  }, [activeAiAgentId, showToast]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const handleToggleActive = async (skill) => {
    try {
      const newStatus = !skill.is_active;

      // Optimistic update
      setSkills((prev) =>
        prev.map((s) => (s.id === skill.id ? { ...s, is_active: newStatus } : s))
      );

      await api.put(`/skills/${skill.id}`, { is_active: newStatus });
      if (showToast) showToast(`Skill '${skill.name}' ${newStatus ? "enabled" : "disabled"}`);
    } catch (err) {
      console.error("Error toggling skill status:", err);
      if (showToast) showToast("Failed to update skill status", "error");
      fetchSkills();
    }
  };

  const openDeleteModal = (skill) => {
    setSkillToDelete(skill);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteSkill = async () => {
    if (!skillToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/skills/${skillToDelete.id}`);
      setSkills((prev) => prev.filter((s) => s.id !== skillToDelete.id));
      if (showToast) showToast(`Skill '${skillToDelete.name}' deleted successfully`);
      setIsDeleteModalOpen(false);
      setSkillToDelete(null);
    } catch (err) {
      console.error("Error deleting skill:", err);
      if (showToast) showToast("Failed to delete skill", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !file.name.endsWith(".md")) {
      if (showToast) showToast("Please upload a valid markdown (.md) file", "error");
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append("file", file);

      await api.request("/skills/upload", {
        method: "POST",
        body: uploadData
      });

      if (showToast) showToast("Skill .md file uploaded successfully");
      await fetchSkills();
    } catch (err) {
      console.error("Error uploading skill file:", err);
      if (showToast) showToast(err.message || "Failed to upload skill file", "error");
    } finally {
      setUploading(false);
    }
  };

  const openCreateModal = () => {
    setEditingSkill(null);
    setFormData({
      name: "",
      description: "",
      instructions: `# Skill Instructions\n\nProvide step-by-step instructions, rules, or references for the AI agent here.\n\nExample:\n- Always format responses using Markdown bullet points.\n- Double-check API parameters before executing queries.`,
      is_active: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    setFormData({
      name: skill.name || "",
      description: skill.description || "",
      instructions: skill.instructions || "",
      is_active: skill.is_active ?? true
    });
    setIsModalOpen(true);
  };

  const handleSaveSkill = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.instructions.trim()) {
      if (showToast) showToast("Please enter both Skill Name and Instructions", "error");
      return;
    }

    setSaving(true);
    try {
      if (editingSkill) {
        await api.put(`/skills/${editingSkill.id}`, formData);
        if (showToast) showToast("Skill updated successfully");
      } else {
        await api.post("/skills", formData);
        if (showToast) showToast("Skill created successfully");
      }

      setIsModalOpen(false);
      await fetchSkills();
    } catch (err) {
      console.error("Error saving skill:", err);
      if (showToast) showToast(err.message || "Failed to save skill", "error");
    } finally {
      setSaving(false);
    }
  };

  const filteredSkills = skills.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.description && s.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );


  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-6xl space-y-12">
        {/* Standard EasyChat Page Header */}
        <PageHeader
          title="Agent Skills"
          description="Upload or author SKILL.md files to equip your AI agent with specialized domain skills, procedures, and capabilities."
          actions={
            <div className="flex items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                accept=".md"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleFileUpload(e.target.files[0]);
                  }
                }}
              />
              <button
                onClick={() => setIsSampleModalOpen(true)}
                title="See Sample Skill"
                className="p-2 text-gray-400 hover:text-gray-900 transition-colors flex items-center justify-center"
              >
                <Eye className="w-5 h-5" />
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 text-xs font-black uppercase tracking-wider transition-all shadow-sm active:scale-95 disabled:opacity-50"
              >
                <Upload className="w-4 h-4 text-gray-600" />
                <span>{uploading ? "Uploading..." : "Upload .md Skill"}</span>
              </button>

              <button
                onClick={openCreateModal}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-black uppercase tracking-wider transition-all shadow-md hover:shadow-lg active:scale-95"
              >
                <Plus className="w-4 h-4" />
                <span>Create Skill</span>
              </button>
            </div>
          }
        />

        {/* Drag & Drop Dropzone */}
        <div
          className={`bg-white rounded-[32px] p-8 border-2 border-dashed transition-all duration-300 relative overflow-hidden group ${dragOver
              ? "border-gray-900 bg-gray-50 ring-4 ring-gray-900/5"
              : "border-gray-100 bg-white hover:border-gray-300"
            } cursor-pointer shadow-xl shadow-gray-100/50`}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
              handleFileUpload(e.dataTransfer.files[0]);
            }
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-[20px] border border-gray-100 flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300 shadow-sm">
              <Upload className="w-8 h-8 text-gray-400 group-hover:text-gray-900 transition-colors" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-1.5 tracking-tight uppercase">
              {dragOver ? "Drop to Ingest" : "Skill Ingestion"}
            </h3>
            <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1 max-w-xs leading-relaxed">
              Drag and drop .md skill files or click to browse
            </p>
            <div className="flex flex-wrap justify-center gap-1.5 mt-4">
              {['SKILL.MD', 'MARKDOWN'].map((ext) => (
                <span key={ext} className="px-3 py-1 bg-gray-100 text-gray-400 rounded-lg text-[9px] font-bold tracking-wider">
                  {ext}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Search & Section Title */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
          <h2 className="text-xs font-black text-gray-900 uppercase tracking-widest flex items-center gap-2">
            <span>Agent Skills</span>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600">
              {filteredSkills.length}
            </span>
          </h2>

          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-xs bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all font-medium"
            />
          </div>
        </div>

        {/* Skills Cards Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-48 bg-gray-100 rounded-[24px] animate-pulse" />
            ))}
          </div>
        ) : filteredSkills.length === 0 ? (
          <div className="bg-white rounded-[32px] p-12 text-center border border-gray-100 shadow-xl shadow-gray-100/50 space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">No Skills Registered Yet</h3>
              <p className="text-xs text-gray-400 max-w-sm mx-auto font-medium">
                Upload a <code className="text-gray-900 font-bold">.md</code> skill file or click "Create Skill" to author specialized agent capabilities.
              </p>
            </div>

          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSkills.map((skill) => (
              <div
                key={skill.id}
                className={`bg-white rounded-[24px] border p-6 flex flex-col justify-between space-y-4 shadow-sm hover:shadow-md ${skill.is_active ? "border-gray-200 hover:border-gray-300" : "border-gray-100 opacity-60 bg-gray-50/50"
                  }`}
              >
                <div className="space-y-3">
                  {/* Card Top Row */}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-black text-gray-900 text-sm leading-snug">{skill.name}</h3>
                      <span className="text-[10px] font-mono text-gray-400">SKILL.md</span>
                    </div>

                    {/* Universal Toggle Switch */}
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={skill.is_active}
                        onChange={() => handleToggleActive(skill)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-emerald-500 transition-colors duration-200 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform duration-200 peer-checked:after:translate-x-5 shadow-sm" />
                    </label>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-gray-500 font-medium line-clamp-2 leading-relaxed">
                    {skill.description || "No description provided."}
                  </p>
                </div>

                {/* Card Footer Actions */}
                <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-1">
                  <button
                    onClick={() => openEditModal(skill)}
                    className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                    title="Edit Skill"
                  >
                    <Edit2 size={16} />
                  </button>

                  <button
                    onClick={() => openDeleteModal(skill)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    title="Delete Skill"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create / Edit Skill Modal */}
        {isModalOpen && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 font-sans">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-900/40 backdrop-blur-md animate-fadeIn"
              onClick={() => setIsModalOpen(false)}
            />

            {/* Modal Card */}
            <div className="relative bg-white rounded-[32px] max-w-2xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh] z-10 animate-slideUp border border-gray-100">
              {/* Modal Header */}
              <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div>
                  <h3 className="font-black text-gray-900 text-sm uppercase tracking-wider">
                    {editingSkill ? "Modify Skill" : "Create New Skill"}
                  </h3>
                  <p className="text-xs font-medium text-gray-400">
                    Define skill instructions and frontmatter for the Agno AI Agent
                  </p>
                </div>

                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSaveSkill} className="flex flex-col flex-1 min-h-0">
                <div className="p-8 space-y-5 overflow-y-auto flex-1">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                      Skill Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. refund_processing, customer_onboarding"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 text-xs font-mono bg-gray-50 border border-gray-100 rounded-xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                      Description
                    </label>
                    <input
                      type="text"
                      placeholder="Short explanation of what this skill enables the agent to do..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 text-xs font-medium bg-gray-50 border border-gray-100 rounded-xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 transition-all"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                        Instructions (SKILL.md Body) *
                      </label>
                      <span className="text-[10px] text-gray-400 font-mono font-bold uppercase">Markdown supported</span>
                    </div>
                    <textarea
                      required
                      rows={6}
                      placeholder="Enter detailed guidelines, rules, and procedures..."
                      value={formData.instructions}
                      onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                      className="w-full p-4 text-xs font-mono bg-slate-900 text-slate-100 border border-slate-800 rounded-2xl focus:outline-none focus:ring-4 focus:ring-gray-900/20 transition-all leading-relaxed"
                    />
                  </div>
                </div>

                {/* Fixed Modal Footer */}

                <div className="px-8 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2.5 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-black uppercase tracking-wider shadow-md transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    <span>{saving ? "Saving..." : "Save Skill"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Sample Skill Viewer Modal */}
        {isSampleModalOpen && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 font-sans">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-900/40 backdrop-blur-md animate-fadeIn"
              onClick={() => setIsSampleModalOpen(false)}
            />

            {/* Modal Card */}
            <div className="relative bg-white rounded-[32px] max-w-2xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh] z-10 animate-slideUp border border-gray-100">
              {/* Modal Header */}
              <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div>
                  <h3 className="font-black text-gray-900 text-sm uppercase tracking-wider">
                    Sample SKILL.md Template
                  </h3>
                  <p className="text-xs font-medium text-gray-400">
                    Standard structure for creating specialized AI agent skills
                  </p>
                </div>

                <button
                  onClick={() => setIsSampleModalOpen(false)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-8 space-y-4 overflow-y-auto flex-1 bg-white">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">SKILL.md Structure</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(SAMPLE_SKILL_MD);
                      if (showToast) showToast("Sample SKILL.md copied to clipboard!");
                    }}
                    title="Copy Sample"
                    className="p-2 text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all border border-gray-200/80 shadow-sm flex items-center justify-center"
                  >
                    <Copy size={15} />
                  </button>
                </div>
                <pre className="text-xs font-mono text-emerald-400 p-6 bg-slate-900 rounded-2xl border border-slate-800 leading-relaxed overflow-x-auto whitespace-pre-wrap shadow-lg">
                  {SAMPLE_SKILL_MD}
                </pre>
              </div>

              {/* Modal Footer */}
              <div className="px-8 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setIsSampleModalOpen(false)}
                  className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSkillToDelete(null);
          }}
          onConfirm={confirmDeleteSkill}
          isLoading={isDeleting}
          title="Delete Agent Skill?"
          message={skillToDelete ? `Are you sure you want to permanently delete the skill '${skillToDelete.name}'? This action cannot be undone.` : "Are you sure you want to delete this skill?"}
        />
      </div>
    </div>
  );
};

export default Skills;
